import React, { useMemo, useRef, useState } from "react";

import { useUpdateOptionGroups } from "./hooks/useUpdateOptionGroups";
import { cn } from "@utils/tailwind.util";
import { CUSTOM_SECTION_TITLES } from "@components/Sections/TagsSection/constants";
import { User, UserForDropdown } from "@app-types/common.type";
import { IPeople } from "@interfaces/people.interface";
import { Checkbox } from "@ui/FormElements";
import { View } from "@ui/View";
import { TagLabel } from "@ui/Content";
import { CompanyAvatar, CompanyAvatarSizes, LetterAvatar } from "@ui/Avatars";

export class OptionGroupsModel {
  users: UserForDropdown[] = [];
  people: UserForDropdown[] = [];
}

interface IAttendeeOptionsProps {
  options: UserForDropdown[];
  selectedOptions: (User | IPeople)[];
  isAllMembersSelected?: boolean;
  onSelect: (value: UserForDropdown) => void;
  searchQuery: string;
  className?: string;
  heightClassName?: string;
  enableCompaniesAndPeople?: boolean;
  isCompact?: boolean;
}

export const AttendeeOptions = ({
  options,
  selectedOptions,
  isAllMembersSelected = false,
  onSelect,
  searchQuery,
  className,
  heightClassName,
  enableCompaniesAndPeople = false,
  isCompact,
}: IAttendeeOptionsProps) => {
  const [filteredOptionGroups, setFilteredOptionGroups] = useState<OptionGroupsModel>(
    new OptionGroupsModel()
  );
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  itemRefs.current = [];

  const finalHeightClassName = heightClassName ?? (isCompact ? "max-h-[240px]" : "");
  const hasOptions = Object.values(filteredOptionGroups).some((group) => group.length > 0);

  const selectedOptionIds = useMemo(() => {
    const selectedOptionIds = selectedOptions.map((option) => option.id);
    if (isAllMembersSelected) selectedOptionIds.push("selectAllOption");

    return selectedOptionIds;
  }, [selectedOptions]);

  useUpdateOptionGroups({ searchQuery, options, selectedOptions, setFilteredOptionGroups });

  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col divide-y divide-stroke-primary dark:divide-gray-600",
        className
      )}
    >
      <div
        role="listbox"
        id="options"
        tabIndex={-1}
        className={cn(finalHeightClassName, "flex-1 overflow-y-auto !outline-none")}
      >
        <View.Condition if={options.length > 0}>
          {Object.keys(filteredOptionGroups).map((group) => {
            const filteredOptions = filteredOptionGroups[group];

            if (!enableCompaniesAndPeople && group === "people") return null;
            if (filteredOptions.length === 0) return null;

            return (
              <div
                key={group}
                className="px-1 pb-1 pt-3 border-b last:border-b-0 dark:border-gray-600"
              >
                <div className="px-2 pb-1.5 text-xs font-medium text-content-secondary dark:text-white uppercase">
                  {CUSTOM_SECTION_TITLES[group] ?? group}
                </div>

                <ul className="space-y-1">
                  {filteredOptions.map((option) => {
                    return (
                      <React.Fragment key={"id" in option ? option.id : ""}>
                        <li
                          role="option"
                          aria-setsize={filteredOptions.length}
                          data-value={"id" in option ? option.id : ""}
                          className="flex items-center gap-2 min-w-0 hover:bg-background-secondary dark:hover:bg-gray-700 aria-selected:bg-background-secondary rounded-md p-2 cursor-pointer"
                          tabIndex={-1}
                          onClick={() => onSelect(option)}
                          onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                        >
                          <Checkbox
                            id={option.id}
                            checked={selectedOptionIds.includes(option.id)}
                            className="cursor-pointer"
                            variant="small"
                            onClick={(e) => {
                              // Prevent click event from bubbling up to the li element when the checkbox is clicked
                              e.stopPropagation();
                              onSelect(option);
                            }}
                            onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                            readOnly
                          />
                          <View.Condition if={option.id !== "selectAllOption"}>
                            {group === "people" ? (
                              <CompanyAvatar
                                logoUrl={option.primary_company?.company?.logo_url}
                                userProps={option}
                                size={CompanyAvatarSizes.SMALL}
                                classNames="w-5 h-5 rounded-xl flex-shrink-0"
                              />
                            ) : (
                              <LetterAvatar
                                user={option}
                                sizeClassName="w-5 h-5 text-[10px] border-none flex-shrink-0 [&:not(:first-child)]:ml-0"
                              />
                            )}
                          </View.Condition>
                          <div className="flex flex-col gap-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 min-w-0">
                              <TagLabel>{option.label}</TagLabel>
                              <View.Condition if={Boolean(option?.title)}>
                                <TagLabel className="font-normal text-content-secondary dark:text-content-secondary">
                                  {option?.title?.title || ""}
                                </TagLabel>
                              </View.Condition>

                              <View.Condition if={Boolean(option?.primary_company)}>
                                <span className="truncate text-gray-450 text-xs">
                                  {option.primary_company?.company?.title || ""}

                                  {option.primary_company?.relation &&
                                  option.primary_company.relation.length > 0 ? (
                                    <span className="ml-1 text-gray-450 text-xs">
                                      • {option.primary_company.relation}
                                    </span>
                                  ) : null}
                                </span>
                              </View.Condition>
                            </div>
                            <View.Condition if={Boolean(group === "people" && option?.email)}>
                              <span className="block truncate text-teal-600 underline text-xs">
                                {option.email}
                              </span>
                            </View.Condition>
                          </div>
                        </li>

                        {option.external && (
                          <li className="w-full rounded-md bg-background-secondary dark:bg-gray-700 p-4 text-sm font-medium text-gray-450 dark:text-gray-200">
                            A new person will be added with this email
                          </li>
                        )}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          <View.Condition if={!hasOptions}>
            <div className="p-4 text-sm text-gray-450 dark:text-gray-200">No matches found</div>
          </View.Condition>
        </View.Condition>
      </div>
    </div>
  );
};
